import React, { useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { runDesignChecks } from '@/lib/designChecks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { BetaInfo } from '@/components/editor/BetaInfo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  FilePlus2,
  Upload,
  Download,
  Network,
  StickyNote,
  Share2,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronDown,
  FileText,
  FileCode2,
  Search,
  ShieldCheck,
  FolderOpen,
  BookOpen,
} from 'lucide-react';

const IconBtn = ({ onClick, title, children, disabled, testid }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className="h-9 w-9"
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={disabled}
    data-testid={testid}
  >
    {children}
  </Button>
);

export const TopToolbar = ({
  onImportClick,
  onExportDrawio,
  onPdfBrowser,
  onPdfServer,
  onShare,
  onNew,
  onOpenProjects,
  onOpenVlan,
  onOpenTitleBlock,
  onOpenSearch,
  onOpenHandbook,
  canvasActive,
}) => {
  const { t, language, setLanguage } = useI18n();
  const name = useStore((s) => s.doc.name);
  const setProjectName = useStore((s) => s.setProjectName);
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const canUndo = useStore((s) => s.past.length > 0);
  const canRedo = useStore((s) => s.future.length > 0);
  const doc = useStore((s) => s.doc);
  const setDesignCheckOpen = useStore((s) => s.setDesignCheckOpen);
  const checks = useMemo(() => runDesignChecks(doc, { lang: language }), [doc, language]);
  const errCount = checks.counts.error;
  const issueCount = checks.counts.error + checks.counts.warning;
  const rf = useReactFlow();

  return (
    <header
      className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-[#FBFBFA] px-3"
      data-testid="top-toolbar"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#111418] text-white">
          <Network className="h-4 w-4" />
        </div>
        <div className="leading-none">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[15px] font-semibold tracking-tight text-[#111418]">SwitchR</span>
            <BetaInfo />
          </div>
          <div className="hidden text-[10px] text-muted-foreground sm:block">{t('app_tagline')}</div>
        </div>
      </div>

      <div className="mx-1 h-7 w-px bg-border" />

      <Input
        value={name}
        onChange={(e) => setProjectName(e.target.value)}
        className="h-9 w-40 text-sm font-medium md:w-56"
        placeholder={t('project_name')}
        data-testid="project-name-input"
        aria-label={t('project_name')}
      />

      <div className="ml-auto flex items-center gap-1.5">
        <IconBtn onClick={onOpenSearch} title={t('search_title')} testid="toolbar-search-button">
          <Search className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={onOpenHandbook} title={t('handbook_open')} testid="toolbar-handbook-button">
          <BookOpen className="h-4 w-4" />
        </IconBtn>
        <Button
          variant="ghost"
          className="relative h-9 px-2.5"
          onClick={() => setDesignCheckOpen(true)}
          data-testid="toolbar-designcheck-button"
          title={t('design_check')}
        >
          <ShieldCheck className="mr-1.5 h-4 w-4" /> <span className="hidden md:inline">{t('design_check')}</span>
          {issueCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none text-white"
              style={{ background: errCount > 0 ? '#B42318' : '#B7791F' }}
              data-testid="designcheck-badge"
            >
              {issueCount}
            </span>
          )}
        </Button>

        <div className="mx-0.5 h-7 w-px bg-border" />

        <Button variant="ghost" className="h-9 px-2.5" onClick={onOpenVlan} data-testid="toolbar-vlan-button">
          <Network className="mr-1.5 h-4 w-4" /> <span className="hidden md:inline">{t('vlan_table')}</span>
        </Button>
        <Button variant="ghost" className="h-9 px-2.5" onClick={onOpenTitleBlock} data-testid="toolbar-titleblock-button">
          <StickyNote className="mr-1.5 h-4 w-4" /> <span className="hidden md:inline">{t('title_block')}</span>
        </Button>

        <div className="mx-0.5 h-7 w-px bg-border" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-9 px-2.5" data-testid="toolbar-file-button">
              <FilePlus2 className="mr-1.5 h-4 w-4" /> <span className="hidden md:inline">{t('file')}</span>
              <ChevronDown className="ml-1 h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onNew} data-testid="menu-new">
              <FilePlus2 className="mr-2 h-4 w-4" /> {t('new_project')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenProjects} data-testid="menu-switch-project">
              <FolderOpen className="mr-2 h-4 w-4" /> {t('switch_project')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onImportClick} data-testid="menu-import">
              <Upload className="mr-2 h-4 w-4" /> {t('import_drawio')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onShare} data-testid="menu-share">
              <Share2 className="mr-2 h-4 w-4" /> {t('share')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-9 px-3" data-testid="toolbar-export-button">
              <Download className="mr-1.5 h-4 w-4" /> {t('export')}
              <ChevronDown className="ml-1 h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExportDrawio} data-testid="menu-export-drawio">
              <FileCode2 className="mr-2 h-4 w-4" /> {t('export_drawio')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">PDF</DropdownMenuLabel>
            <DropdownMenuItem onClick={onPdfBrowser} data-testid="menu-export-pdf-browser">
              <FileText className="mr-2 h-4 w-4" /> {t('export_pdf_browser')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPdfServer} data-testid="menu-export-pdf-server">
              <FileText className="mr-2 h-4 w-4" /> {t('export_pdf_server')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="mx-0.5 h-7 w-px bg-border" />

        <IconBtn onClick={undo} disabled={!canUndo} title={t('undo')} testid="toolbar-undo-button">
          <Undo2 className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={redo} disabled={!canRedo} title={t('redo')} testid="toolbar-redo-button">
          <Redo2 className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={() => rf && rf.zoomIn()} disabled={!canvasActive} title={t('zoom_in')} testid="toolbar-zoom-in-button">
          <ZoomIn className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={() => rf && rf.zoomOut()} disabled={!canvasActive} title={t('zoom_out')} testid="toolbar-zoom-out-button">
          <ZoomOut className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={() => rf && rf.fitView({ padding: 0.15 })} disabled={!canvasActive} title={t('fit_view')} testid="toolbar-fit-button">
          <Maximize2 className="h-4 w-4" />
        </IconBtn>

        <div className="mx-0.5 h-7 w-px bg-border" />

        <ToggleGroup
          type="single"
          value={language}
          onValueChange={(v) => v && setLanguage(v)}
          className="rounded-md border border-border bg-white"
          data-testid="toolbar-language-toggle"
        >
          <ToggleGroupItem value="de" data-testid="toolbar-language-de" className="h-8 w-9 text-xs data-[state=on]:bg-[#111418] data-[state=on]:text-white">
            DE
          </ToggleGroupItem>
          <ToggleGroupItem value="en" data-testid="toolbar-language-en" className="h-8 w-9 text-xs data-[state=on]:bg-[#111418] data-[state=on]:text-white">
            EN
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </header>
  );
};
